/**
 * Does deleting THIS conversation actually free the bytes it is charged for?
 *
 * ── The thing that makes it not obvious ──────────────────────────────────
 *
 * A member's CAS bytes are deduped across their own conversations: the same
 * file in two chats is charged to the FIRST one only, and the second shows
 * 0 (`ROW_NUMBER() OVER (PARTITION BY content_hash …)`, the chat-storage
 * listing). So the row showing 500 MB is the holder — and deleting it does
 * NOT free 500 MB, because the copy in the other chat becomes the holder
 * and is charged instead. The total does not move.
 *
 * That is the report behind todo item 4: "I deleted a 500 MB conversation
 * and the number did not change", after a dialog that had just promised
 * "this gives you back 500 MB".
 *
 * Inline bytes are NOT deduped (they live in the message part, one copy
 * each), so they always free on delete and never make this true.
 */
export interface SharedCheckFile {
  /** The CAS handle — a content hash. `null` for inline bytes. */
  handle: string | null
  /** What this conversation is actually charged for this file. */
  chargedBytes: number
}

export interface SharedCheckSession {
  sessionId: string
  files: SharedCheckFile[]
}

/**
 * True when this conversation is CHARGED for a file that also sits in one of
 * the member's other listed conversations.
 *
 * Both halves are required:
 *  · charged here (`chargedBytes > 0`) — a copy already charged elsewhere
 *    frees nothing anyway, and the dialog has a separate line for that;
 *  · present elsewhere — otherwise the delete really does free the bytes.
 */
export function freesLessThanItSays(
  session: SharedCheckSession,
  allSessions: SharedCheckSession[],
): boolean {
  const elsewhere = new Set<string>()
  for (const s of allSessions) {
    if (s.sessionId === session.sessionId) continue
    for (const f of s.files) if (f.handle) elsewhere.add(f.handle)
  }
  return session.files.some((f) => f.handle !== null && f.chargedBytes > 0 && elsewhere.has(f.handle))
}

/**
 * What deleting this conversation ACTUALLY frees.
 *
 * ⚠️ THE HEADLINE FIGURE WAS THE BUG, not the missing caveat. The dialog
 * said "this gives you back 500 MB", where 500 MB is what the conversation
 * is CHARGED — and a shared file is charged here only because this
 * conversation is the first reference. Delete it and the charge moves to
 * the other copy, so those bytes never come back. Appending a caveat to a
 * wrong number explains the number instead of fixing it (Season,
 * 2026-10-23).
 *
 * Freed = the charged bytes of every file that is NOT also in another of
 * her conversations. Inline bytes always count: they are never deduped,
 * one real copy per conversation, so they always free.
 */
export function freedByDeleting(
  session: SharedCheckSession,
  allSessions: SharedCheckSession[],
): number {
  const elsewhere = new Set<string>()
  for (const s of allSessions) {
    if (s.sessionId === session.sessionId) continue
    for (const f of s.files) if (f.handle) elsewhere.add(f.handle)
  }
  let freed = 0
  for (const f of session.files) {
    if (f.chargedBytes <= 0) continue
    if (f.handle !== null && elsewhere.has(f.handle)) continue
    freed += f.chargedBytes
  }
  return freed
}
